import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useSendTest } from '@/api/integrations/use-integrations';
import type { TenantIntegration } from '@/api/integrations/integrationService';
import { getTestFields, type IntegrationChannel } from './catalog';
import { buildFieldsSchema, type FormValues } from './integrationHelpers';

interface SendTestDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    row: TenantIntegration;
    channel: IntegrationChannel;
    providerName: string;
}

/**
 * Confirmation + inputs for a real test send. Unlike "Check connection" this really sends
 * an email / SMS / message / call, so the dialog says so and asks for the target.
 */
export const SendTestDialog: React.FC<SendTestDialogProps> = ({
    open,
    onOpenChange,
    row,
    channel,
    providerName,
}) => {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const sendTest = useSendTest();
    const [failure, setFailure] = useState<string | undefined>();

    const fields = useMemo(
        () => getTestFields(channel, row.provider_name),
        [channel, row.provider_name]
    );
    const schema = useMemo(
        () =>
            buildFieldsSchema(fields, {
                required: t('integrations.validation.required'),
                email: t('integrations.validation.email'),
                url: t('integrations.validation.url'),
                number: t('integrations.validation.number'),
                option: t('integrations.validation.option'),
            }),
        [fields, t]
    );

    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: Object.fromEntries(fields.map(field => [field.key, ''])),
    });

    useEffect(() => {
        if (open) {
            setFailure(undefined);
            form.reset();
        }
    }, [open, form]);

    const onSubmit = form.handleSubmit(async values => {
        setFailure(undefined);
        const payload = Object.fromEntries(
            Object.entries(values).filter(([, value]) => String(value).trim() !== '')
        );
        try {
            const result = await sendTest.mutateAsync({ id: row.id, data: payload });
            if (result?.success === false) {
                setFailure(result.message || t('integrations.test.failed'));
                return;
            }
            enqueueSnackbar(result?.message || t('integrations.test.sent'), { variant: 'success' });
            onOpenChange(false);
        } catch (error: any) {
            setFailure(error?.response?.data?.message || t('integrations.test.failed'));
        }
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{t(`integrations.verify.send.${channel}`)}</DialogTitle>
                    <DialogDescription>
                        {t('integrations.test.description', { name: providerName })}
                    </DialogDescription>
                </DialogHeader>

                <Alert variant="warning">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{t(`integrations.test.warning.${channel}`)}</AlertDescription>
                </Alert>

                <Form {...form}>
                    <form
                        onSubmit={event => {
                            // React events bubble through portals: don't submit the drawer form.
                            event.stopPropagation();
                            void onSubmit(event);
                        }}
                        className="space-y-4"
                        noValidate
                    >
                        {fields.map(field => (
                            <FormField
                                key={field.key}
                                control={form.control}
                                name={field.key}
                                render={({ field: control }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t(field.label)}
                                            {field.required && (
                                                <span
                                                    className="ms-1 text-destructive"
                                                    aria-hidden="true"
                                                >
                                                    *
                                                </span>
                                            )}
                                        </FormLabel>
                                        <FormControl>
                                            {field.type === 'textarea' ? (
                                                <Textarea
                                                    {...control}
                                                    rows={3}
                                                    placeholder={field.placeholder}
                                                />
                                            ) : (
                                                <Input
                                                    {...control}
                                                    type={field.type === 'email' ? 'email' : 'text'}
                                                    dir={field.type === 'email' ? 'ltr' : undefined}
                                                    placeholder={field.placeholder}
                                                    autoComplete="off"
                                                />
                                            )}
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        ))}

                        {failure && (
                            <Alert variant="destructive">
                                <XCircle className="h-4 w-4" />
                                <AlertDescription>{failure}</AlertDescription>
                            </Alert>
                        )}

                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                {t('actions.cancel')}
                            </Button>
                            <Button type="submit" disabled={sendTest.isPending}>
                                {sendTest.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <CheckCircle2 className="h-4 w-4" />
                                )}
                                {t('integrations.test.confirm')}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default SendTestDialog;
